export function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min)) + min;
}

export function getRandomArbitrary(min, max) {
  return getRandomInt(min, max);
}

export function getRanges(arParams) {
  return arParams.range.slice(0, arParams.operandsCount).map((item) => ({
    min: Math.ceil(item.from),
    max: Math.ceil(item.to),
  }));
}

export function buildRanges(arParams) {
  return getRanges(arParams);
}

export function createTask(operands, res) {
  return {
    resShow: false,
    data: {
      operands,
      res,
    },
  };
}

export function createErrorTask(message) {
  return {
    resShow: true,
    data: {
      operands: [message],
      res: "",
    },
  };
}

function toDigits(value) {
  return String(value)
    .split("")
    .reverse()
    .map((digit) => Number(digit));
}

function countCarries(a, b) {
  const len = Math.max(String(a).length, String(b).length);
  const left = toDigits(String(a).padStart(len, "0"));
  const right = toDigits(String(b).padStart(len, "0"));
  let carry = 0;
  let count = 0;

  for (let i = 0; i < len; i++) {
    const sum = left[i] + right[i] + carry;
    if (sum >= 10) {
      carry = 1;
      count += 1;
    } else {
      carry = 0;
    }
  }

  return count;
}

function countBorrows(a, b) {
  const len = Math.max(String(a).length, String(b).length);
  const left = toDigits(String(a).padStart(len, "0"));
  const right = toDigits(String(b).padStart(len, "0"));
  let borrow = 0;
  let count = 0;

  for (let i = 0; i < len; i++) {
    const minuend = left[i] - borrow;

    if (minuend < right[i]) {
      borrow = 1;
      count += 1;
    } else {
      borrow = 0;
    }
  }

  return count;
}

function withRetryLimit(taskCount, generator, maxAttempts = taskCount * 200) {
  const tasks = [];
  let attempts = 0;

  while (tasks.length < taskCount && attempts < maxAttempts) {
    const task = generator();
    attempts += 1;

    if (task) {
      tasks.push(task);
    }
  }

  return {
    tasks,
    exhausted: tasks.length < taskCount,
  };
}

function generateTasks(taskCount, factory) {
  const tasks = [];

  for (let i = 0; i < taskCount; i++) {
    tasks.push(factory());
  }

  return tasks;
}

function generateFilteredTasks(taskCount, factory, maxAttempts) {
  return withRetryLimit(taskCount, factory, maxAttempts);
}

function generateUniqueTasks(taskCount, factory, keyFactory, maxAttempts = taskCount * 200) {
  const tasks = [];
  const seen = new Set();
  let attempts = 0;

  while (tasks.length < taskCount && attempts < maxAttempts) {
    const task = factory();
    attempts += 1;

    if (!task) {
      continue;
    }

    const key = keyFactory(task);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    tasks.push(task);
  }

  return {
    tasks,
    exhausted: tasks.length < taskCount,
  };
}

function createSummationGenerator(arParams) {
  const ranges = getRanges(arParams);
  const difficulty = arParams.difficulty;

  return () => {
    const operands = [];
    let res = 0;

    for (let y = 0; y < arParams.operandsCount; y++) {
      const operand = getRandomInt(ranges[y].min, ranges[y].max);
      operands.push(operand);
      res += operand;
    }

    if (!difficulty) {
      return createTask(operands, res);
    }

    const carries = countCarries(operands[0], operands[1]);
    const isValid =
      (difficulty === "no-carry" && carries === 0) ||
      (difficulty === "single-carry" && carries === 1) ||
      (difficulty === "chain-carry" && carries >= 2);

    return isValid ? createTask(operands, res) : null;
  };
}

function createSubtractionGenerator(arParams) {
  const ranges = getRanges(arParams);
  const difficulty = arParams.difficulty;

  return () => {
    const a = getRandomInt(ranges[0].min, ranges[0].max);
    const b = getRandomInt(ranges[1].min, ranges[1].max);

    if (a < b) {
      return null;
    }

    const res = a - b;

    if (!difficulty) {
      return createTask([a, b], res);
    }

    const borrows = countBorrows(a, b);
    const isValid =
      (difficulty === "no-borrow" && borrows === 0) ||
      (difficulty === "single-borrow" && borrows === 1) ||
      (difficulty === "chain-borrow" && borrows >= 2);

    return isValid ? createTask([a, b], res) : null;
  };
}

export function generateSummationTasks(arParams) {
  if (!arParams.difficulty) {
    const ranges = getRanges(arParams);

    return generateTasks(arParams.taskCount, () => {
      const operands = [];
      let res = 0;

      for (let y = 0; y < arParams.operandsCount; y++) {
        const operand = getRandomInt(ranges[y].min, ranges[y].max);
        operands.push(operand);
        res += operand;
      }

      return createTask(operands, res);
    });
  }

  const result = generateFilteredTasks(
    arParams.taskCount,
    createSummationGenerator(arParams)
  );

  return result.exhausted
    ? [createErrorTask("Не удалось сгенерировать примеры для этого диапазона")]
    : result.tasks;
}

export function generateSubtractionTasks(arParams) {
  if (!arParams.difficulty) {
    const ranges = getRanges(arParams);

    return generateTasks(arParams.taskCount, () => {
      const a = getRandomInt(ranges[0].min, ranges[0].max);
      const b = getRandomInt(ranges[1].min, ranges[1].max);
      const res = a - b;

      if (res < 0) {
        return null;
      }

      return createTask([a, b], res);
    }).filter(Boolean);
  }

  const result = generateFilteredTasks(
    arParams.taskCount,
    createSubtractionGenerator(arParams)
  );

  return result.exhausted
    ? [createErrorTask("Не удалось сгенерировать примеры для этого диапазона")]
    : result.tasks;
}

export function generateMultiplicationTasks(arParams) {
  const ranges = getRanges(arParams);
  const shouldSortOperands = Boolean(arParams.sortOperands);

  const result = generateUniqueTasks(
    arParams.taskCount,
    () => {
      const a = getRandomInt(ranges[0].min, ranges[0].max);
      const b = getRandomInt(ranges[1].min, ranges[1].max);
      const operands = shouldSortOperands ? [Math.min(a, b), Math.max(a, b)] : [a, b];

      return createTask(operands, operands[0] * operands[1]);
    },
    (task) => task.data.operands.join("x")
  );

  return result.exhausted
    ? [createErrorTask("Не удалось сгенерировать примеры для этого диапазона")]
    : result.tasks;
}

export function generateDivisionTasks(arParams) {
  const ranges = getRanges(arParams);
  return generateFilteredTasks(arParams.taskCount, () => {
    const a = getRandomInt(ranges[0].min, ranges[0].max);
    const b = getRandomInt(ranges[1].min, ranges[1].max);

    if (a % b !== 0) {
      return null;
    }

    return createTask([a, b], a / b);
  });
}

export function generatePercentageTasks(arParams) {
  const ranges = getRanges(arParams);
  const percentValues = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95];

  return generateFilteredTasks(arParams.taskCount, () => {
    const a = percentValues[Math.floor(Math.random() * percentValues.length)];
    const b = getRandomInt(ranges[1].min, ranges[1].max);
    const res = (a * b) / 100;

    if (!Number.isInteger(res)) {
      return null;
    }

    return createTask([a, b], res);
  });
}

export function generateHowManyPercentTasks(arParams) {
  const ranges = getRanges(arParams);

  return generateFilteredTasks(arParams.taskCount, () => {
    const a = getRandomInt(ranges[0].min, ranges[0].max);
    const b = getRandomInt(ranges[1].min, ranges[1].max);
    const res = ((a - b) / b) * 100;

    if (!Number.isInteger(res)) {
      return null;
    }

    return createTask([a, b], res);
  });
}

export function generateConversionTasks(arParams, currencyExchangeRate = 75) {
  const ranges = getRanges(arParams);

  return generateTasks(arParams.taskCount, () => {
    const a = getRandomInt(ranges[0].min, ranges[0].max);
    return createTask([a, 0], a * currencyExchangeRate);
  });
}
